import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  MessageSquare,
  Plus,
  Star,
  AlertTriangle,
  Trophy,
  TrendingUp,
  Upload,
  X,
  ArrowLeft,
  RefreshCw,
  Filter,
  Search,
  Paperclip,
  Clock
} from 'lucide-react';

// Feedback tags configuration
const FEEDBACK_TAGS = {
  points_forts: {
    label: "Points forts",
    color: "bg-green-100 text-green-700 border-green-200",
    icon: Star,
    iconColor: "text-green-600",
    types: ['recognition', 'success', 'positive'] // Map types to this tag
  },
  a_ameliorer: {
    label: "À améliorer",
    color: "bg-orange-100 text-orange-700 border-orange-200",
    icon: TrendingUp,
    iconColor: "text-orange-600",
    types: ['improvement', 'development', 'growth']
  },
  action_urgente: {
    label: "Action urgente",
    color: "bg-red-100 text-red-700 border-red-200",
    icon: AlertTriangle,
    iconColor: "text-red-600",
    types: ['urgent', 'critical', 'warning']
  },
  succes_important: {
    label: "Succès important",
    color: "bg-blue-100 text-blue-700 border-blue-200",
    icon: Trophy,
    iconColor: "text-blue-600",
    types: ['achievement', 'milestone', 'feedback'] // General feedback goes here
  }
};

// Helper function to get tag key from feedback type
const getTagFromType = (type) => {
  if (!type) return null;
  const typeLower = type.toLowerCase();
  for (const [key, tag] of Object.entries(FEEDBACK_TAGS)) {
    if (tag.types?.includes(typeLower)) {
      return key;
    }
  }
  return null;
};

export default function ContinuousFeedbackPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [feedbackList, setFeedbackList] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // New feedback form
  const [newFeedback, setNewFeedback] = useState({
    toEmployeeId: '',
    campaignId: '',
    type: 'feedback',
    tag: 'points_forts',
    message: '',
    fileIds: []
  });
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchData();
  }, [user?.companyId, filterEmployee, filterTag]);

  const fetchData = async () => {
    if (!user?.companyId) return;
    setLoading(true);
    
    try {
      let feedbackUrl = `/continuous-feedback?company_id=${user.companyId}`;
      if (filterEmployee) feedbackUrl += `&employee_id=${filterEmployee}`;
      if (filterTag) feedbackUrl += `&tag=${filterTag}`;
      
      const [feedbackRes, employeesRes, campaignsRes] = await Promise.all([
        api.get(feedbackUrl),
        api.get(`/employees?companyId=${user.companyId}`),
        api.get(`/campaigns?company_id=${user.companyId}&status=active`)
      ]);
      
      setFeedbackList(feedbackRes.data.feedback || []);
      setEmployees(employeesRes.data.employees || []);
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    setUploading(true);
    
    for (const file of files) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} est trop volumineux (max 10MB)`);
        continue;
      }
      
      // Check file type
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/gif'];
      if (!allowedTypes.includes(file.type)) {
        toast.error(`Type de fichier non supporté: ${file.name}`);
        continue;
      }
      
      try {
        const formData = new FormData();
        formData.append('file', file);
        
        const response = await api.post(
          `/continuous-feedback/upload-file?company_id=${user.companyId}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );
        
        setUploadedFiles(prev => [...prev, {
          id: response.data.fileId,
          name: file.name,
          url: response.data.url
        }]);
        
        setNewFeedback(prev => ({
          ...prev,
          fileIds: [...prev.fileIds, response.data.fileId]
        }));
        
      } catch (error) {
        toast.error(`Erreur upload: ${file.name}`);
      }
    }
    
    setUploading(false);
    e.target.value = ''; // Reset input
  };

  const removeFile = (fileId) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
    setNewFeedback(prev => ({
      ...prev,
      fileIds: prev.fileIds.filter(id => id !== fileId)
    }));
  };

  const handleCreateFeedback = async () => {
    if (!newFeedback.toEmployeeId) {
      toast.error('Veuillez sélectionner un collaborateur');
      return;
    }
    if (!newFeedback.message.trim()) {
      toast.error('Veuillez saisir un message');
      return;
    }
    
    setSubmitting(true);
    try {
      await api.post(
        `/continuous-feedback?company_id=${user.companyId}&from_user_id=${user.id}`,
        newFeedback
      );
      
      toast.success('Feedback envoyé avec succès');
      setShowCreateDialog(false);
      setNewFeedback({
        toEmployeeId: '',
        campaignId: '',
        type: 'feedback',
        tag: 'points_forts',
        message: '',
        fileIds: []
      });
      setUploadedFiles([]);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)}j`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const filteredFeedback = feedbackList.filter(fb => {
    if (searchQuery) {
      const content = fb.content || fb.message || '';
      const managerName = fb.managerName || fb.fromUserName || '';
      return content.toLowerCase().includes(searchQuery.toLowerCase()) ||
             managerName.toLowerCase().includes(searchQuery.toLowerCase());
    }
    return true;
  });

  // Group by employee - support both employeeId and toEmployeeId
  const groupedByEmployee = filteredFeedback.reduce((acc, fb) => {
    const empId = fb.employeeId || fb.toEmployeeId;
    if (!acc[empId]) {
      const emp = employees.find(e => e.id === empId);
      acc[empId] = {
        employee: emp,
        feedback: []
      };
    }
    acc[empId].feedback.push(fb);
    return acc;
  }, {});

  if (loading) {
    return (
      <DashboardLayout title="Feedback continu">
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="w-8 h-8 animate-spin text-webtech-blue" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Feedback continu">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/performance')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour
            </Button>
            <div>
              <h1 className="text-2xl font-heading font-bold text-slate-900">
                Feedback continu
              </h1>
              <p className="text-slate-500">
                Suivez et documentez la performance tout au long de l'année
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setShowCreateDialog(true)}
            className="bg-webtech-blue hover:bg-webtech-blue-hover"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau feedback
          </Button>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-4 flex-wrap">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          
          <Select value={filterEmployee || "all"} onValueChange={(v) => setFilterEmployee(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[200px]">
              <SelectValue placeholder="Tous les employés" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les employés</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>
                  {emp.firstName} {emp.lastName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select value={filterTag || "all"} onValueChange={(v) => setFilterTag(v === "all" ? "" : v)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tous les tags" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les tags</SelectItem>
              {Object.entries(FEEDBACK_TAGS).map(([key, tag]) => (
                <SelectItem key={key} value={key}>{tag.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Tag Stats */}
        <div className="grid grid-cols-4 gap-4">
          {Object.entries(FEEDBACK_TAGS).map(([key, tag]) => {
            const Icon = tag.icon;
            // Count by tag OR by mapped type
            const count = feedbackList.filter(fb => 
              fb.tag === key || 
              tag.types?.includes(fb.type?.toLowerCase())
            ).length;
            return (
              <Card 
                key={key} 
                className={`cursor-pointer transition-all ${filterTag === key ? 'ring-2 ring-webtech-blue' : ''}`}
                onClick={() => setFilterTag(filterTag === key ? '' : key)}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${tag.color.split(' ')[0]}`}>
                        <Icon className={`w-5 h-5 ${tag.iconColor}`} />
                      </div>
                      <div>
                        <p className="font-medium text-sm">{tag.label}</p>
                        <p className="text-2xl font-bold">{count}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Feedback List */}
        <div className="space-y-6">
          {Object.keys(groupedByEmployee).length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <MessageSquare className="w-12 h-12 text-slate-300 mb-4" />
                <p className="text-slate-500 mb-4">Aucun feedback pour le moment</p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Créer le premier feedback
                </Button>
              </CardContent>
            </Card>
          ) : (
            Object.values(groupedByEmployee).map(({ employee, feedback }) => (
              <Card key={employee?.id || 'unknown'}>
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full bg-webtech-blue flex items-center justify-center text-white font-medium">
                      {employee?.firstName?.[0]}{employee?.lastName?.[0]}
                    </div>
                    <div>
                      <span>{employee?.firstName} {employee?.lastName}</span>
                      <p className="text-sm font-normal text-slate-500">{employee?.position}</p>
                    </div>
                    <Badge variant="outline" className="ml-auto">{feedback.length} feedback(s)</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {feedback.map(fb => {
                    // Get tag from tag field or map from type
                    const tagKey = fb.tag || getTagFromType(fb.type);
                    const tagInfo = FEEDBACK_TAGS[tagKey];
                    const Icon = tagInfo?.icon || Star;
                    
                    return (
                      <div key={fb.id} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                        <div className={`p-2 rounded-lg ${tagInfo?.color.split(' ')[0] || 'bg-slate-100'}`}>
                          <Icon className={`w-4 h-4 ${tagInfo?.iconColor || 'text-slate-600'}`} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={tagInfo?.color || 'bg-slate-100'}>
                              {tagInfo?.label || fb.tag}
                            </Badge>
                            <span className="text-xs text-slate-400 flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {formatDate(fb.createdAt)}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700">{fb.content || fb.message}</p>
                          {fb.fileIds && fb.fileIds.length > 0 && (
                            <div className="flex items-center gap-2 mt-2">
                              <Paperclip className="w-3 h-3 text-slate-400" />
                              <span className="text-xs text-slate-400">{fb.fileIds.length} fichier(s) joint(s)</span>
                            </div>
                          )}
                          <p className="text-xs text-slate-400 mt-2">Par {fb.managerName || fb.fromUserName || 'Manager'}</p>
                        </div>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))
          )}
        </div>

        {/* Create Feedback Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Nouveau feedback</DialogTitle>
              <DialogDescription>
                Documentez un événement, un succès ou un point d'amélioration
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Collaborateur *</Label>
                <Select 
                  value={newFeedback.toEmployeeId}
                  onValueChange={(v) => setNewFeedback({ ...newFeedback, toEmployeeId: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un collaborateur" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>
                        {emp.firstName} {emp.lastName} - {emp.position}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Campagne associée</Label>
                <Select 
                  value={newFeedback.campaignId || "none"}
                  onValueChange={(v) => setNewFeedback({ ...newFeedback, campaignId: v === "none" ? "" : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner une campagne (optionnel)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucune campagne</SelectItem>
                    {campaigns.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Type de feedback *</Label>
                <div className="grid grid-cols-2 gap-2">
                  {Object.entries(FEEDBACK_TAGS).map(([key, tag]) => {
                    const Icon = tag.icon;
                    return (
                      <button
                        key={key}
                        type="button"
                        className={`flex items-center gap-2 p-3 rounded-lg border transition-all ${
                          newFeedback.tag === key 
                            ? `${tag.color} border-2` 
                            : 'bg-white border-slate-200 hover:bg-slate-50'
                        }`}
                        onClick={() => setNewFeedback({ ...newFeedback, tag: key })}
                      >
                        <Icon className={`w-4 h-4 ${tag.iconColor}`} />
                        <span className="text-sm font-medium">{tag.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Message *</Label>
                <Textarea
                  placeholder="Décrivez l'événement, le comportement observé..."
                  value={newFeedback.message}
                  onChange={(e) => setNewFeedback({ ...newFeedback, message: e.target.value })}
                  rows={4}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Fichiers joints</Label>
                <div 
                  className="border-2 border-dashed rounded-lg p-4 text-center cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png,.gif"
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                  <Upload className="w-8 h-8 mx-auto text-slate-400 mb-2" />
                  <p className="text-sm text-slate-500">
                    {uploading ? 'Upload en cours...' : 'Cliquez pour ajouter des fichiers'}
                  </p>
                  <p className="text-xs text-slate-400">PDF, Images (max 10MB)</p>
                </div>
                
                {uploadedFiles.length > 0 && (
                  <div className="space-y-2 mt-2">
                    {uploadedFiles.map(file => (
                      <div key={file.id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                        <div className="flex items-center gap-2">
                          <Paperclip className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{file.name}</span>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(file.id)}
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                Annuler
              </Button>
              <Button 
                onClick={handleCreateFeedback}
                disabled={submitting}
                className="bg-webtech-blue hover:bg-webtech-blue-hover"
              >
                {submitting ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  'Envoyer le feedback'
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
